import React, { type ReactElement, useState } from 'react';
import { Collapse } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import qs from 'qs';
import Router from 'next/router';
import {
  faAngleDown,
  faAngleLeft,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import type { QuerySpecification } from 'components/article/articleListController';

interface Facet {
  count: number;
  description: string;
  localizedName: string;
  name: string;
  treePath: string;
  id: string;
  children?: { [key: string]: Facet };
}

function FacetItem({
  facet,
  onChange,
  querySpecification,
  name,
}: {
  facet: Facet;
  onChange: Function;
  querySpecification: QuerySpecification;

  name: string;
}) {
  const activeQuerySpecification = (querySpecification[name] || {}) as {
    iLike?: string;
  };
  const isActive = _.isEqual(activeQuerySpecification, {
    iLike: `${facet.treePath}%`,
  });
  const hasActiveChildren = _.includes(
    activeQuerySpecification?.iLike,
    facet.treePath,
  );

  const [isNavigationOpen, setisNavigationOpen] = useState(
    isActive || hasActiveChildren,
  );
  const hasChildren = facet.children && Object.keys(facet.children).length > 0;

  const handleChange = (newValue: any) => {
    if (isActive) {
      onChange('');
    } else {
      onChange(newValue);
    }

    if (hasChildren) {
      setisNavigationOpen(true);
    }
  };
  if (hasChildren) {
    return (
      <>
        <h6
          className={`ms-2 mb-0 mb-1 link-animated link-animated-light fw-light ${
            isActive ? 'navigation-button-active' : 'navigation-button-inactive'
          }`}
          onClick={() => {
            handleChange(facet.treePath);
          }}
          style={{ cursor: 'pointer' }}
        >
          {facet.name}

          <span className="float-end">
            {isActive && (
              <span
                className="pe-3"
                onClick={e => {
                  e.stopPropagation(); // prevent the parent onClick from firing
                  onChange('');
                }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </span>
            )}
            <span
              onClick={e => {
                e.stopPropagation(); // prevent the parent onClick from firing
                setisNavigationOpen(!isNavigationOpen);
              }}
            >
              <FontAwesomeIcon
                icon={isNavigationOpen ? faAngleDown : faAngleLeft}
              />
            </span>
          </span>
        </h6>

        <Collapse isOpen={isNavigationOpen}>
          <NavigationFacet
            facetChildren={facet.children ? Object.values(facet.children) : []}
            onChange={onChange}
            name={name}
            querySpecification={querySpecification}
          />
        </Collapse>
      </>
    );
  }
  return (
    <>
      <h6
        className={`ms-3 mb-0 link-animated link-animated-light fw-light text-muted ${
          isActive ? 'navigation-button-active' : 'navigation-button-inactive'
        }`}
        onClick={() => {
          handleChange(facet.treePath);
        }}
        style={{ cursor: 'pointer' }}
      >
        {facet.name}
        {isActive && (
          <span className="float-end">
            <FontAwesomeIcon icon={faXmark} />
          </span>
        )}
      </h6>
    </>
  );
}

function NavigationFacet({
  facetChildren,
  onChange,
  querySpecification,
  name,
}: {
  facetChildren: Facet[];
  onChange: Function;
  querySpecification: QuerySpecification;
  name: string;
}) {
  return (
    <>
      <div className="mb-2">
        {_.map(facetChildren, (child: Facet) => {
          return (
            <FacetItem
              facet={child}
              onChange={onChange}
              querySpecification={querySpecification}
              name={name}
            />
          );
        })}
      </div>
    </>
  );
}
const getCleanedConfigQuery = (
  querySpecification: QuerySpecification,
  name: string,
) => {
  const configQuery = querySpecification?.[name];
  if (!configQuery) return null;
  const configQueryValue = _.get(configQuery, 'iLike', '');
  return configQueryValue.replace('%', '');
};

function NavigationPanel({
  facets,
  onChange,
  name,
  querySpecification,
  articleListConfig,
  children,
}: {
  facets: Facet[];
  onChange: Function;
  name: string;
  querySpecification: QuerySpecification;
  articleListConfig: any;
  children: ReactElement;
}) {
  const [isNavigationOpen, setisNavigationOpen] = useState(true);

  const handleOnChange = (newValue: any) => {
    const configQuery = getCleanedConfigQuery(
      articleListConfig.querySpecification,
      name,
    );

    const navigatingOutsidePageQuerySpecification =
      !_.includes(newValue, configQuery) || newValue === '';

    if (navigatingOutsidePageQuerySpecification) {
      const query = qs.stringify({
        querySpecification: JSON.stringify({
          [name]: { iLike: `${newValue}%` },
        }),
      });
      Router.push({
        pathname: '/articles',
        query,
      });
      return;
    }
    if (newValue) {
      onChange(name, { iLike: `${newValue}%` });
    } else {
      onChange(name, { iLike: `%` });
    }
  };

  return (
    <div className="mb-2 ps-2" key="facet-navigation">
      <h6
        className="pe-1 mb-2 fw-bold text-dark"
        onClick={() => setisNavigationOpen(!isNavigationOpen)}
        style={{ cursor: 'pointer' }}
      >
        Kategorier
        <span className="float-end">
          <FontAwesomeIcon
            icon={isNavigationOpen ? faAngleDown : faAngleLeft}
          />
        </span>
      </h6>
      <Collapse isOpen={isNavigationOpen}>
        {children}
        <NavigationFacet
          facetChildren={facets}
          onChange={handleOnChange}
          querySpecification={querySpecification}
          name={name}
        />
      </Collapse>
    </div>
  );
}

export default NavigationPanel;
